import React from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import { STUDIO_STAGES, type StageDef, type StageKey, type StageStatus } from './studioStages';

/**
 * STAGE STEPPER (RF3) — the production journey made visible, and navigable.
 *
 * Two jobs: tell the creator where they are in a production, and let them move. It is NOT a wizard — every
 * stage stays clickable, because a creator revising a line of dialogue after locking shots is normal work,
 * not an error. Done stages get a check; the current one is marked; the rest stay quiet.
 *
 * ONE ROW, FLAT (owner 2026-08-23, screenshot93: "can be flat, and more space efficient, use less height").
 * This used to be two stacked bands under a third "Video Studio" title bar — 149px of chrome above the work
 * on a workstation, where canvas height is the scarce resource. It is now a single line that lives IN the
 * Studio's header: stages, then what this page is for, then the way forward. Nothing was dropped to get there
 * — the step number, the done check and the sub-labels ("written", "2 cast") are what tell a creator what is
 * finished, so they moved onto the same line instead of stacking under the label.
 *
 * The active stage is marked by WEIGHT AND A QUIET GROUND, not the filled black pill it used to wear: in a
 * header bar a raised pill reads as a button you must press, which is exactly the wizard feeling the stepper
 * is meant to avoid.
 *
 * Presentational only — the parent owns the current stage (SteveJobs layering). The rail still scrolls
 * horizontally on a phone rather than wrapping into a second row that pushes the work off screen.
 */

/** One stage. Number → check as it completes; the sub-label rides alongside instead of underneath. */
const StageTab: React.FC<{
  stage: StageDef;
  step: number;
  current: boolean;
  status: StageStatus;
  onGo: () => void;
}> = ({ stage, step, current, status, onGo }) => (
  <button
    type="button"
    role="tab"
    aria-selected={current}
    aria-label={`${stage.label}${status.detail ? ` — ${status.detail}` : ''}`}
    onClick={onGo}
    className={`flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 transition-colors ${
      current
        ? 'bg-secondary-100 text-secondary-900 dark:bg-secondary-800 dark:text-white'
        : 'text-secondary-500 hover:text-secondary-900 dark:text-secondary-400 dark:hover:text-white'
    }`}
  >
    <span
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${TYPE.micro} ${
        status.done && !current
          ? 'bg-secondary-200 text-secondary-600 dark:bg-secondary-700 dark:text-secondary-200'
          : 'border border-current'
      }`}
    >
      {status.done && !current ? <Check className="h-2.5 w-2.5" /> : step}
    </span>
    <span className={`${TYPE.control} ${current ? 'font-semibold' : 'font-medium'}`}>{stage.label}</span>
    {status.detail && <span className={`${TYPE.micro} text-secondary-400`}>{status.detail}</span>}
  </button>
);

const StageStepper: React.FC<{
  current: StageKey;
  statuses: Record<StageKey, StageStatus>;
  onGo: (key: StageKey) => void;
}> = ({ current, statuses, onGo }) => {
  const index = STUDIO_STAGES.findIndex((s) => s.key === current);
  const prev = index > 0 ? STUDIO_STAGES[index - 1] : null;
  const next = index >= 0 && index < STUDIO_STAGES.length - 1 ? STUDIO_STAGES[index + 1] : null;

  return (
    <div className="flex min-w-0 items-center gap-2">
      {/* The rail. Scrolls on a phone; on a workstation it NEVER shrinks (BUGS.md 2026-09-27: with a series
          crumb in front of it the rail was squeezed until the Shots tab was cut off and the hint sat over it).
          The purpose hint and the series crumb are what give way. */}
      <div
        className="scrollbar-hide -mx-1 flex min-w-0 items-center gap-0.5 overflow-x-auto px-1 lg:shrink-0 lg:overflow-visible"
        role="tablist"
        aria-label="Production stages"
      >
        {STUDIO_STAGES.map((s, i) => (
          <StageTab
            key={s.key}
            stage={s}
            step={i + 1}
            current={s.key === current}
            status={statuses[s.key]}
            onGo={() => onGo(s.key)}
          />
        ))}
      </div>

      {/* What this page is for. One sentence beats a manual — kept, but it only claims room where there is
          room: below a workstation width the stages and the way forward win the line, and on a workstation it is
          the FIRST thing to yield — it truncates down to nothing rather than pushing into the tabs. */}
      <p className={`hidden min-w-0 max-w-[18rem] flex-1 truncate ${TYPE.meta} text-secondary-400 lg:block`}>
        {STUDIO_STAGES[index >= 0 ? index : 0].purpose}
      </p>

      {/* The way forward (and back). */}
      <div className="flex shrink-0 items-center gap-0.5">
        {prev && (
          <button type="button" onClick={() => onGo(prev.key)} aria-label={`Back to ${prev.label}`}
            className={`flex items-center gap-0.5 rounded-md px-1.5 py-1 ${TYPE.meta} text-secondary-500 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-800`}>
            <ChevronLeft className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{prev.label}</span>
          </button>
        )}
        {next && (
          <button type="button" onClick={() => onGo(next.key)}
            className={`flex items-center gap-0.5 rounded-md bg-secondary-100 px-2 py-1 ${TYPE.meta} font-medium text-secondary-900 hover:bg-secondary-200 dark:bg-secondary-800 dark:text-white dark:hover:bg-secondary-700`}>
            {next.label} <ChevronRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default StageStepper;
